/**
 * src/pages/Review.tsx
 *
 * Tab 5 — Review Calibration. Six sections:
 *
 *   1. Calibration corpus counter — "X / 200 reviewed deals" — Tony's
 *      tuning-freeze widget. Suggestions section greys out until ≥ 200.
 *   2. Approval rate by DCS band — bar chart, raise-floor candidates
 *      highlighted.
 *   3. Approval rate by (source × category) — heatmap.
 *   4. Reject-reason donut — not_genuine / bad_seller / wrong_category /
 *      other / unspecified.
 *   5. Suggested tuning — read-only text suggestions; locked until corpus
 *      hits target.
 *   6. Recent decisions feed — last 50 reviewed deals.
 *
 * Note: the wrong-decision flag aggregation section in the plan
 * (Tab 5 §5) lives in the Deal Explorer module; the Calibration tab
 * links across to /deals when that PR lands.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Lock, Sparkles, Unlock } from 'lucide-react';
import { request } from '@/api/client';
import { cn } from '@/lib/cn';
import { relativeTime, num } from '@/lib/format';

// Inline types — Tab 5 API surface is small enough that a separate
// dbview.ts split would be overkill.

interface BandRow {
  label: string;
  lo: number | null;
  hi: number | null;
  total: number;
  approved: number;
  rejected: number;
  approval_rate: number | null;
  raise_floor_candidate: boolean;
}

interface SCCell {
  source: string;
  category: string;
  total: number;
  approved: number;
  approval_rate: number;
}

interface RecentRow {
  unique_id: string;
  source: string;
  title: string;
  reviewStatus: 'approved' | 'rejected';
  reviewReason?: string;
  reviewedBy?: number;
  reviewedAt?: string;
  dcs?: number;
  category?: string;
}

interface CorpusResp {
  ok: true;
  reviewed: number;
  target: number;
  ratio: number;
  unlocked: boolean;
}

interface RejectReasonRow {
  reason: string;
  n: number;
}

interface SuggestionsResp {
  ok: true;
  unlocked: boolean;
  reviewed: number;
  target: number;
  suggestions: Array<{
    kind: 'raise_floor';
    band: string;
    approval_rate: number;
    total: number;
    message: string;
  }>;
}

const SINCE_OPTIONS: Array<{ label: string; ms: number | undefined }> = [
  { label: 'all',  ms: undefined          },
  { label: '7d',   ms: 7 * 86_400_000     },
  { label: '30d',  ms: 30 * 86_400_000    },
  { label: '90d',  ms: 90 * 86_400_000    },
];

const REJECT_COLOR: Record<string, string> = {
  not_genuine:    '#fb7185',
  bad_seller:     '#fb923c',
  wrong_category: '#fbbf24',
  other:          '#94a3b8',
  unspecified:    '#475569',
};

export default function Review() {
  const [sinceMs, setSinceMs] = useState<number | undefined>(undefined);

  return (
    <div className="space-y-4 max-w-7xl">
      <header className="flex items-start sm:items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">Review calibration</h1>
          <p className="text-sm text-slate-500 mt-1">
            Approval rates · reject reasons · tuning suggestions
          </p>
        </div>
        <div className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded p-0.5">
          {SINCE_OPTIONS.map(o => (
            <button
              key={o.label}
              onClick={() => setSinceMs(o.ms)}
              className={cn(
                'px-2 py-0.5 text-xs rounded',
                sinceMs === o.ms ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </header>

      <CorpusCounter sinceMs={sinceMs} />
      <BandsPanel sinceMs={sinceMs} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SourceCategoryHeatmap sinceMs={sinceMs} />
        <RejectReasonDonut sinceMs={sinceMs} />
      </div>
      <SuggestionsPanel sinceMs={sinceMs} />
      <RecentFeed />
    </div>
  );
}

// ─── Corpus counter ──────────────────────────────────────────────────────────

function CorpusCounter({ sinceMs }: { sinceMs: number | undefined }) {
  const { data, isLoading } = useQuery({
    queryKey: ['review', 'corpus'],
    queryFn:  () => request<CorpusResp>('/v1/observe/dbview/review/corpus'),
    refetchInterval: 60_000,
  });

  // 2026-05-18 — Tony's "this is broken" reaction was driven by the corpus
  // counter (19k all-time) sitting next to bands showing only 1-2 reviews
  // in the 7d window. The corpus is intentionally all-time (governance
  // gate for tuning unlock — Tony, 2026-05-07), but reading that next to
  // a windowed view felt inconsistent. We pull the windowed total from
  // the bands query (cache-shared with BandsPanel below — react-query
  // dedupes) and surface BOTH numbers + an explainer so the relationship
  // is obvious. No backend change — corpus query stays all-time.
  const windowedQ = useQuery({
    queryKey: ['review', 'bands', sinceMs],
    queryFn:  () => request<{ ok: true; rows: BandRow[]; total_reviewed: number }>(
      `/v1/observe/dbview/review/by-band${sinceMs ? `?sinceMs=${sinceMs}` : ''}`,
    ),
    refetchInterval: 60_000,
  });
  const windowedCount = windowedQ.data?.total_reviewed ?? null;
  const windowLabel = sinceMs
    ? (SINCE_OPTIONS.find(o => o.ms === sinceMs)?.label ?? 'window')
    : 'all-time';

  if (isLoading || !data) {
    return (
      <div className="bg-slate-900/40 border border-slate-800 rounded-md p-4 flex items-center gap-3 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading corpus…
      </div>
    );
  }

  const pct = Math.min(100, Math.round(data.ratio * 100));

  return (
    <div className={cn(
      'border rounded-md p-4',
      data.unlocked ? 'bg-emerald-900/20 border-emerald-800' : 'bg-slate-900/40 border-slate-800',
    )}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {data.unlocked
            ? <Unlock className="w-4 h-4 text-emerald-400" />
            : <Lock   className="w-4 h-4 text-slate-500" />}
          <span className="text-xs uppercase tracking-wider text-slate-400">
            calibration corpus
            <span className="ml-1 text-slate-600 normal-case tracking-normal">· all-time</span>
          </span>
        </div>
        <span className="text-2xl font-semibold tabular-nums text-slate-100">
          {data.reviewed.toLocaleString()}{' '}
          <span className="text-sm text-slate-500">/ {data.target.toLocaleString()}</span>
        </span>
      </div>
      <div className="mt-2 h-2 bg-slate-900 rounded overflow-hidden">
        <div
          className={cn(
            'h-full rounded transition-all',
            data.unlocked ? 'bg-emerald-500' : 'bg-amber-500',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-slate-500 mt-2">
        {data.unlocked
          ? 'Tuning suggestions unlocked. Threshold changes can be considered.'
          : `Tuning frozen — ${data.target - data.reviewed} more reviews needed before suggestions unlock (Tony, 2026-05-07).`}
      </p>
      <div className="mt-2 pt-2 border-t border-slate-800/60 flex items-baseline justify-between gap-2 flex-wrap text-xs">
        <span className="text-slate-500">
          Reviewed in selected window ({windowLabel})
        </span>
        <span className="font-mono text-slate-300 tabular-nums">
          {windowedCount === null ? '—' : windowedCount.toLocaleString()}
        </span>
      </div>
      <p className="text-[11px] text-slate-600 mt-1 leading-snug">
        The corpus counter is intentionally all-time — it's the gate for unlocking
        tuning suggestions. The panels below (bands, source × category, reject reasons)
        respect the window selector at the top of the page. Empty bands while the
        corpus is large just means the selected window is sparse, not that anything
        is broken.
      </p>
    </div>
  );
}

// ─── Bands panel ─────────────────────────────────────────────────────────────

function BandsPanel({ sinceMs }: { sinceMs: number | undefined }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['review', 'bands', sinceMs],
    queryFn:  () => request<{ ok: true; rows: BandRow[]; total_reviewed: number }>(
      `/v1/observe/dbview/review/by-band${sinceMs ? `?sinceMs=${sinceMs}` : ''}`,
    ),
    refetchInterval: 60_000,
  });

  return (
    <section className="bg-slate-900/40 border border-slate-800 rounded-md p-3">
      <h2 className="text-xs font-semibold tracking-wider text-slate-400 uppercase mb-2">
        Approval rate by DCS band
      </h2>
      {isLoading && <RowLoading label="Computing…" />}
      {isError   && <RowError   label="Failed to load." />}
      {data && (
        <>
          <div className="space-y-1">
            {data.rows.map(b => <BandRowRender key={b.label} row={b} />)}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            {data.total_reviewed.toLocaleString()} reviewed in window. Bands marked
            <span className="mx-1 text-emerald-400">↑</span>are raise-floor candidates
            (≥80% approval, ≥10 reviews).
          </p>
        </>
      )}
    </section>
  );
}

function BandRowRender({ row }: { row: BandRow }) {
  const empty = row.total === 0;
  const pct = row.approval_rate === null ? 0 : row.approval_rate * 100;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="font-mono w-16 text-slate-300">{row.label}</span>
      <div className="flex-1 bg-slate-900 rounded h-4 overflow-hidden flex">
        {row.approved > 0 && (
          <div
            className="bg-emerald-500/80 h-full flex items-center justify-end pr-1 text-[10px] tabular-nums"
            style={{ width: `${(row.approved / Math.max(row.total, 1)) * 100}%` }}
            title={`${row.approved} approved`}
          >
            {row.approved}
          </div>
        )}
        {row.rejected > 0 && (
          <div
            className="bg-rose-500/80 h-full flex items-center justify-start pl-1 text-[10px] tabular-nums"
            style={{ width: `${(row.rejected / Math.max(row.total, 1)) * 100}%` }}
            title={`${row.rejected} rejected`}
          >
            {row.rejected}
          </div>
        )}
      </div>
      <span className={cn(
        'tabular-nums w-14 text-right',
        empty ? 'text-slate-600' : 'text-slate-300',
      )}>
        {empty ? '—' : `${pct.toFixed(0)}%`}
      </span>
      <span className="w-4 text-center">
        {row.raise_floor_candidate
          ? <span className="text-emerald-400" title="Raise-floor candidate">↑</span>
          : <span className="text-slate-700">·</span>}
      </span>
    </div>
  );
}

// ─── Source × category heatmap ───────────────────────────────────────────────

function SourceCategoryHeatmap({ sinceMs }: { sinceMs: number | undefined }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['review', 'sc', sinceMs],
    queryFn:  () => request<{ ok: true; cells: SCCell[] }>(
      `/v1/observe/dbview/review/by-source-category${sinceMs ? `?sinceMs=${sinceMs}` : ''}`,
    ),
    refetchInterval: 60_000,
  });

  return (
    <section className="bg-slate-900/40 border border-slate-800 rounded-md p-3">
      <h2 className="text-xs font-semibold tracking-wider text-slate-400 uppercase mb-2">
        Approval × source × category
      </h2>
      {isLoading && <RowLoading label="Computing…" />}
      {isError   && <RowError   label="Failed to load." />}
      {data && (data.cells.length === 0
        ? <div className="text-xs text-slate-500">Not enough reviewed deals to populate cells.</div>
        : <SCGrid cells={data.cells} />)}
    </section>
  );
}

function SCGrid({ cells }: { cells: SCCell[] }) {
  const sources    = [...new Set(cells.map(c => c.source))].sort();
  const categories = [...new Set(cells.map(c => c.category))].sort();
  const map = new Map<string, SCCell>();
  for (const c of cells) map.set(`${c.source}|${c.category}`, c);

  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse">
        <thead className="text-slate-400">
          <tr>
            <th className="text-left px-2 py-1 font-medium sticky left-0 bg-slate-900 z-10">source ↓ / cat →</th>
            {categories.map(c => (
              <th key={c} className="px-2 py-1 font-mono text-[10px] text-center min-w-[5rem]">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sources.map(s => (
            <tr key={s} className="border-t border-slate-800">
              <td className="px-2 py-1 font-mono text-slate-200 sticky left-0 bg-slate-950/95 z-10">{s}</td>
              {categories.map(c => {
                const cell = map.get(`${s}|${c}`);
                if (!cell) {
                  return <td key={c} className="px-2 py-1 text-slate-700 text-center">—</td>;
                }
                const r = cell.approval_rate;
                return (
                  <td
                    key={c}
                    className="px-2 py-1 text-center tabular-nums"
                    style={{ background: rateColor(r), color: r > 0.5 ? '#0f172a' : '#cbd5e1' }}
                    title={`${cell.approved} / ${cell.total} approved`}
                  >
                    {(r * 100).toFixed(0)}%
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Approval-rate ramp: red (0%) → amber (50%) → emerald (100%). */
function rateColor(r: number): string {
  if (r < 0.5) {
    const t = r / 0.5;
    const red   = Math.round(244 - (244 - 251) * t);
    const green = Math.round(63  + (191 - 63 ) * t);
    const blue  = Math.round(94  + (36  - 94 ) * t);
    return `rgba(${red},${green},${blue},${0.25 + 0.5 * t})`;
  }
  const t = (r - 0.5) / 0.5;
  const red   = Math.round(251 - (251 - 52)  * t);
  const green = Math.round(191 + (211 - 191) * t);
  const blue  = Math.round(36  + (153 - 36)  * t);
  return `rgba(${red},${green},${blue},${0.5 + 0.5 * t})`;
}

// ─── Reject-reason donut ─────────────────────────────────────────────────────

function RejectReasonDonut({ sinceMs }: { sinceMs: number | undefined }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['review', 'reasons', sinceMs],
    queryFn:  () => request<{ ok: true; rows: RejectReasonRow[]; total: number }>(
      `/v1/observe/dbview/review/reject-reasons${sinceMs ? `?sinceMs=${sinceMs}` : ''}`,
    ),
    refetchInterval: 60_000,
  });

  return (
    <section className="bg-slate-900/40 border border-slate-800 rounded-md p-3">
      <h2 className="text-xs font-semibold tracking-wider text-slate-400 uppercase mb-2">
        Reject reason distribution
      </h2>
      {isLoading && <RowLoading label="Computing…" />}
      {isError   && <RowError   label="Failed to load." />}
      {data && (data.total === 0
        ? <div className="text-xs text-slate-500">No rejections in window.</div>
        : <Donut total={data.total} rows={data.rows.filter(r => r.n > 0)} />
      )}
    </section>
  );
}

function Donut({ total, rows }: { total: number; rows: RejectReasonRow[] }) {
  let acc = 0;
  const stops = rows.map(r => {
    const start = (acc / total) * 360;
    acc += r.n;
    const end = (acc / total) * 360;
    return `${REJECT_COLOR[r.reason] ?? '#64748b'} ${start}deg ${end}deg`;
  }).join(', ');

  return (
    <div className="flex items-center gap-4">
      <div
        className="w-24 h-24 rounded-full shrink-0"
        style={{ background: `conic-gradient(${stops})` }}
      >
        <div className="w-full h-full flex items-center justify-center">
          <div className="bg-slate-900 rounded-full w-14 h-14 flex items-center justify-center text-xs tabular-nums text-slate-300">
            {total.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-1 text-xs">
        {rows.map(r => (
          <div key={r.reason} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-sm" style={{ background: REJECT_COLOR[r.reason] ?? '#64748b' }} />
            <span className="font-mono text-slate-300 flex-1">{r.reason}</span>
            <span className="tabular-nums text-slate-400">
              {r.n.toLocaleString()}{' '}
              <span className="text-slate-600">({((r.n / total) * 100).toFixed(0)}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Suggestions panel ───────────────────────────────────────────────────────

function SuggestionsPanel({ sinceMs }: { sinceMs: number | undefined }) {
  const { data, isLoading } = useQuery({
    queryKey: ['review', 'sug', sinceMs],
    queryFn:  () => request<SuggestionsResp>(
      `/v1/observe/dbview/review/suggestions${sinceMs ? `?sinceMs=${sinceMs}` : ''}`,
    ),
    refetchInterval: 60_000,
  });

  return (
    <section className={cn(
      'border rounded-md p-3',
      data?.unlocked
        ? 'bg-slate-900/40 border-slate-800'
        : 'bg-slate-900/20 border-slate-800/50 opacity-70',
    )}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-3.5 h-3.5 text-amber-400" />
        <h2 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
          Suggested tuning
        </h2>
        {data && !data.unlocked && (
          <span className="text-[10px] text-amber-300 ml-auto">
            🔒 frozen — {data.target - data.reviewed} more reviews needed
          </span>
        )}
      </div>
      {isLoading && <RowLoading label="Computing…" />}
      {data && (data.suggestions.length === 0
        ? <div className="text-xs text-slate-500">No raise-floor candidates yet.</div>
        : (
          <ul className="space-y-1 text-xs">
            {data.suggestions.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-emerald-400 shrink-0">↑</span>
                <span className="text-slate-300">{s.message}</span>
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}

// ─── Recent feed ─────────────────────────────────────────────────────────────

function RecentFeed() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['review', 'recent'],
    queryFn:  () => request<{ ok: true; rows: RecentRow[] }>(
      '/v1/observe/dbview/review/recent?limit=50',
    ),
    refetchInterval: 30_000,
  });

  return (
    <section className="bg-slate-900/40 border border-slate-800 rounded-md p-3">
      <h2 className="text-xs font-semibold tracking-wider text-slate-400 uppercase mb-2">
        Recent decisions
      </h2>
      {isLoading && <RowLoading label="Loading…" />}
      {isError   && <RowError   label="Failed to load." />}
      {data && (data.rows.length === 0
        ? <div className="text-xs text-slate-500">No reviewed deals yet.</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left  px-2 py-1 font-medium">when</th>
                  <th className="text-left  px-2 py-1 font-medium">status</th>
                  <th className="text-left  px-2 py-1 font-medium">source</th>
                  <th className="text-right px-2 py-1 font-medium">DCS</th>
                  <th className="text-left  px-2 py-1 font-medium">title</th>
                  <th className="text-left  px-2 py-1 font-medium">reason</th>
                  <th className="text-right px-2 py-1 font-medium">tg</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.unique_id} className="border-t border-slate-800">
                    <td className="px-2 py-1 text-slate-400 whitespace-nowrap">
                      {r.reviewedAt ? relativeTime(r.reviewedAt) : '—'}
                    </td>
                    <td className={cn(
                      'px-2 py-1 font-mono',
                      r.reviewStatus === 'approved' ? 'text-emerald-400' : 'text-rose-400',
                    )}>{r.reviewStatus}</td>
                    <td className="px-2 py-1 font-mono text-slate-200">{r.source}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-300">
                      {num(r.dcs)}
                    </td>
                    <td className="px-2 py-1 text-slate-200 max-w-[24rem] truncate">{r.title}</td>
                    <td className="px-2 py-1 text-slate-400">{r.reviewReason ?? '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-500">
                      {r.reviewedBy ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}

function RowLoading({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
      <Loader2 className="w-4 h-4 animate-spin" />
      {label}
    </div>
  );
}

function RowError({ label }: { label: string }) {
  return <div className="text-sm text-rose-300 py-3">{label}</div>;
}
